import { z } from "zod";

/**
 * Validation message keys for i18n translation.
 * These are used as keys that get translated at display time.
 */
export const VALIDATION_KEYS = {
  required: "onboarding.validation.required",
  minLength: "onboarding.validation.minLength",
  maxLength: "onboarding.validation.maxLength",
  invalidEmail: "onboarding.validation.invalidEmail",
  invalidFormat: "onboarding.validation.invalidFormat",
  invalidFile: "onboarding.validation.invalidFile",
  fileTooLarge: "onboarding.validation.fileTooLarge",
  passwordMismatch: "onboarding.validation.passwordMismatch",
} as const;

export type FieldType =
  | "text"
  | "email"
  | "password"
  | "tel"
  | "number"
  | "select"
  | "switch"
  | "checkbox"
  | "textarea"
  | "file";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface FieldValidation {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  patternMessage?: string;
  /** Field name that this field must match (for password confirmation) */
  matchField?: string;
  custom?: (value: unknown) => string | undefined;
}

export interface FormFieldConfig {
  name: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  description?: string;
  /** Extended help text shown in collapsible section for AAA compliance */
  extendedDescription?: string;
  defaultValue?: unknown;
  validation?: FieldValidation;
  options?: SelectOption[];
  sensitive?: boolean;
  showPasswordToggle?: boolean;
  /** Make field read-only (pre-filled, cannot be edited) */
  readonly?: boolean;
  dependsOn?: {
    field: string;
    condition: "equals" | "notEquals" | "truthy" | "falsy";
    value?: unknown;
  };
  // File input specific
  accept?: string;
  maxFileSize?: number;
}

export interface FormStepConfig {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  fields: FormFieldConfig[];
  disablePersistence?: boolean;
}

export interface FormConfig {
  id: string;
  title: string;
  description?: string;
  steps: FormStepConfig[];
  submitButtonText?: string;
  successMessage?: string;
}

export interface FormValues {
  [key: string]: unknown;
}

export interface FormEngineState {
  currentStep: number;
  values: FormValues;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  isSubmitting: boolean;
  isValid: boolean;
}

export function generateZodSchema(fields: FormFieldConfig[]): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const schemaFields: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    let fieldSchema: z.ZodTypeAny;

    switch (field.type) {
      case "email":
        // Use i18n key for email validation
        fieldSchema = z.string().email(VALIDATION_KEYS.invalidEmail);
        break;
      case "number":
        fieldSchema = z.coerce.number();
        if (field.validation?.min !== undefined) {
          fieldSchema = (fieldSchema as z.ZodNumber).min(field.validation.min);
        }
        if (field.validation?.max !== undefined) {
          fieldSchema = (fieldSchema as z.ZodNumber).max(field.validation.max);
        }
        break;
      case "switch":
      case "checkbox":
        fieldSchema = z.boolean();
        break;
      case "select":
        if (field.options && field.options.length > 0) {
          const values = field.options.map((o) => o.value);
          fieldSchema = z.enum(values as [string, ...string[]], {
            message: VALIDATION_KEYS.required,
          });
        } else {
          fieldSchema = z.string();
        }
        break;
      case "file":
        fieldSchema = z.instanceof(File, { message: VALIDATION_KEYS.required }).refine(
          (file) => {
            if (field.accept) {
              const acceptedTypes = field.accept.split(",").map((t) => t.trim().toLowerCase());
              const fileExt = `.${file.name.split(".").pop()?.toLowerCase()}`;
              const fileMime = file.type.toLowerCase();
              return acceptedTypes.some((t) => t === fileExt || t === fileMime);
            }
            return true;
          },
          { message: VALIDATION_KEYS.invalidFile }
        ).refine(
          (file) => {
            if (field.maxFileSize) {
              return file.size <= field.maxFileSize;
            }
            return true;
          },
          { message: VALIDATION_KEYS.fileTooLarge }
        );
        break;
      default:
        fieldSchema = z.string();
    }

    if (field.type === "text" || field.type === "password" || field.type === "textarea") {
      if (field.validation?.minLength) {
        // Use i18n key with placeholder for min length
        fieldSchema = (fieldSchema as z.ZodString).min(
          field.validation.minLength,
          VALIDATION_KEYS.minLength
        );
      }
      if (field.validation?.maxLength) {
        // Use i18n key with placeholder for max length
        fieldSchema = (fieldSchema as z.ZodString).max(
          field.validation.maxLength,
          VALIDATION_KEYS.maxLength
        );
      }
      if (field.validation?.pattern) {
        // Use patternMessage if provided, otherwise use i18n key
        fieldSchema = (fieldSchema as z.ZodString).regex(
          new RegExp(field.validation.pattern),
          field.validation.patternMessage || VALIDATION_KEYS.invalidFormat
        );
      }
    }

    if (field.validation?.required) {
      if (field.type === "switch" || field.type === "checkbox") {
        fieldSchema = (fieldSchema as z.ZodBoolean).refine((val) => val === true, {
          message: VALIDATION_KEYS.required,
        });
      } else if (typeof fieldSchema === "object" && "min" in fieldSchema) {
        fieldSchema = (fieldSchema as z.ZodString).min(1, VALIDATION_KEYS.required);
      }
    } else if (field.type !== "switch" && field.type !== "checkbox") {
      fieldSchema = fieldSchema.optional();
    }

    schemaFields[field.name] = fieldSchema;
  }

  return z.object(schemaFields);
}

export function generateFullSchema(config: FormConfig): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const allFields = config.steps.flatMap((step) => step.fields);
  return generateZodSchema(allFields);
}

export function getDefaultValues(config: FormConfig): FormValues {
  const values: FormValues = {};
  for (const step of config.steps) {
    for (const field of step.fields) {
      if (field.defaultValue !== undefined) {
        values[field.name] = field.defaultValue;
      } else {
        switch (field.type) {
          case "switch":
          case "checkbox":
            values[field.name] = false;
            break;
          case "number":
            values[field.name] = 0;
            break;
          case "file":
            values[field.name] = null;
            break;
          default:
            values[field.name] = "";
        }
      }
    }
  }
  return values;
}
